import { useState, useEffect } from "react";
import { Camera, Upload, ArrowLeft, Loader2 } from "lucide-react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import { toast } from "react-toastify";
import { uploadRiderDocument } from '../utils/uploadRiderDocument';

const AddNewDriver = ({ isEditMode = false }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    dob: "",
    nextOfKinName: "",
    nextOfKinPhone: "",
    nin: "",
    stickerNumber: "",
    stickerColour: "green_white",
    guarantor1Name: "",
    guarantor1Phone: "",
    guarantor2Name: "",
    guarantor2Phone: "",
    referralCode: "",
    password: "",
    licenseFile: null,
    passportFile: null,
    profileImage: null,
  });
  const [existingDocs, setExistingDocs] = useState({
    driverLicenseUrl: "",
    passportPhotoUrl: "",
  });
  const [loadingData, setLoadingData] = useState(isEditMode);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // In edit mode, load the rider's existing profile (by RiderProfile id) and
  // pre-populate the form.
  useEffect(() => {
    if (!isEditMode || !id) return;
    let active = true;
    (async () => {
      try {
        setLoadingData(true);
        const res = await api.get(`/v1/admin/riders/${id}`);
        const r = res.data?.data || res.data;
        if (!active || !r) return;
        const toDateInput = (d) => {
          if (!d) return "";
          const dt = new Date(d);
          return isNaN(dt.getTime()) ? "" : dt.toISOString().slice(0, 10);
        };
        setFormData((prev) => ({
          ...prev,
          name: r.user?.name || "",
          email: r.user?.email || "",
          phone: r.user?.phone || "",
          dob: toDateInput(r.dob || r.user?.dateOfBirth),
          nextOfKinName: r.nextOfKinName || "",
          nextOfKinPhone: r.nextOfKinPhone || "",
          nin: r.nin || "",
          stickerNumber: r.stickerNumber || "",
          stickerColour: r.stickerColour || "green_white",
          guarantor1Name: r.guarantor1Name || "",
          guarantor1Phone: r.guarantor1Phone || "",
          guarantor2Name: r.guarantor2Name || "",
          guarantor2Phone: r.guarantor2Phone || "",
          referralCode: r.referralCode || "",
        }));
        setExistingDocs({
          driverLicenseUrl: r.documents?.driverLicenseUrl || "",
          passportPhotoUrl: r.documents?.passportPhotoUrl || "",
        });
      } catch (err) {
        console.error("Error loading rider for edit:", err);
        toast.error(
          err.response?.data?.message || "Failed to load rider details",
        );
      } finally {
        if (active) setLoadingData(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [isEditMode, id]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      setFormData({ ...formData, [name]: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // ── Edit mode: update the existing rider profile ──────────────────────
      if (isEditMode) {
        if (!formData.name || !formData.email || !formData.phone) {
          toast.error('Name, email and phone are required');
          setIsSubmitting(false);
          return;
        }

        // Only upload documents that were re-selected; keep the rest as-is.
        let driverLicenseUrl;
        let passportPhotoUrl;
        if (formData.licenseFile) {
          driverLicenseUrl = await uploadRiderDocument(formData.licenseFile, 'driver_license');
        }
        if (formData.passportFile) {
          passportPhotoUrl = await uploadRiderDocument(formData.passportFile, 'passport');
        }

        const editBody = {
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          dob: formData.dob || undefined,
          nextOfKinName: formData.nextOfKinName.trim() || undefined,
          nextOfKinPhone: formData.nextOfKinPhone.trim() || undefined,
          nin: formData.nin.trim() || undefined,
          stickerNumber: formData.stickerNumber.trim() || undefined,
          stickerColour: formData.stickerColour,
          guarantor1Name: formData.guarantor1Name.trim() || undefined,
          guarantor1Phone: formData.guarantor1Phone.trim() || undefined,
          guarantor2Name: formData.guarantor2Name.trim() || undefined,
          guarantor2Phone: formData.guarantor2Phone.trim() || undefined,
          referralCode: formData.referralCode.trim() || undefined,
          driverLicenseUrl,
          passportPhotoUrl,
        };
        Object.keys(editBody).forEach((key) => {
          if (editBody[key] === undefined || editBody[key] === '') {
            delete editBody[key];
          }
        });

        await api.put(`/v1/admin/riders/${id}/profile`, editBody);
        toast.success('Rider updated successfully!');
        navigate('/drivers');
        return;
      }

      // Mirrors CreateRiderDto exactly. Previously this demanded a date of
      // birth and a driver's licence that the API does not require and the
      // rider app never collects, while letting NIN and sticker details
      // through even though the API rejects without them — so the form failed
      // valid riders and accepted invalid ones.
      const missing = [
        [!formData.name, 'Name'],
        [!formData.email, 'Email'],
        [!formData.phone, 'Phone'],
        [!formData.nin, 'NIN'],
        [!formData.stickerNumber, 'Sticker number'],
        [!formData.stickerColour, 'Sticker colour'],
      ]
        .filter(([bad]) => bad)
        .map(([, label]) => label);

      if (missing.length) {
        toast.error(`Required: ${missing.join(', ')}`);
        setIsSubmitting(false);
        return;
      }

      if (!formData.passportFile) {
        toast.error('A passport photograph is required');
        setIsSubmitting(false);
        return;
      }

      // Upload files to S3
      console.log('Uploading rider documents…');
      const [driverLicenseUrl, passportPhotoUrl] = await Promise.all([
        uploadRiderDocument(formData.licenseFile, 'driver_license'),
        uploadRiderDocument(formData.passportFile, 'passport')
      ]);

      if (!driverLicenseUrl || !passportPhotoUrl) {
        throw new Error('File upload failed');
      }

      console.log('Files uploaded successfully');
      console.log('Driver License URL:', driverLicenseUrl);
      console.log('Passport Photo URL:', passportPhotoUrl);

      // Prepare request body
      const requestBody = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        dob: formData.dob,
        nextOfKinName: formData.nextOfKinName.trim(),
        nextOfKinPhone: formData.nextOfKinPhone.trim(),
        nin: formData.nin.trim(),
        stickerNumber: formData.stickerNumber.trim() || undefined,
        stickerColour: formData.stickerColour,
        guarantor1Name: formData.guarantor1Name.trim(),
        guarantor1Phone: formData.guarantor1Phone.trim(),
        guarantor2Name: formData.guarantor2Name.trim() || undefined,
        guarantor2Phone: formData.guarantor2Phone.trim() || undefined,
        referralCode: formData.referralCode.trim() || undefined,
        // Omitted when blank so the API falls back to generating one.
        password: formData.password?.trim() || undefined,
        driverLicenseUrl,
        passportPhotoUrl
      };

      // Remove undefined fields
      Object.keys(requestBody).forEach(key => {
        if (requestBody[key] === undefined || requestBody[key] === '') {
          delete requestBody[key];
        }
      });

      // Send to backend
      const response = await api.post('/v1/admin/riders', requestBody);

      console.log('Server response:', response.data);

      toast.success('Driver added successfully!');
      navigate("/drivers");

    } catch (error) {
      // The full axios error carries the request body (password) and auth header.
      console.error("Error adding rider:", error.response?.status, error.message);

      let errorMessage = `Failed to ${isEditMode ? 'update' : 'add'} driver. Please try again.`;

      // Handle specific error codes
      if (error.response?.status === 403) {
        errorMessage = 'Access Forbidden: You do not have permission to perform this action. Please contact your administrator.';
        console.error('403 Forbidden - Permission denied');
      } else if (error.response?.status === 500) {
        errorMessage = 'Server Error: The backend encountered an error. Please try again or contact support.';
        console.error('500 Internal Server Error');
      } else if (error.response?.data) {
        console.error('Backend response data:', error.response.data);
        console.error('Backend validation errors:', error.response.data.details);
        errorMessage = error.response.data.message || 'Validation failed';

        if (Array.isArray(error.response.data.details)) {
          console.error('Detailed errors:');
          error.response.data.details.forEach(detail => {
            console.error(`  - ${detail}`);
          });
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingData) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col w-full lg:pl-64">
          <Topbar />
          <main className="flex-1 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
            <span className="ml-2 text-gray-600">Loading rider details...</span>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col w-full lg:pl-64">
        <Topbar />
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/drivers")}
                className="p-2 rounded-lg hover:bg-gray-100"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">
                  {isEditMode ? "Edit Rider" : "Add New Rider"}
                </h1>
                <p className="text-sm text-gray-500">
                  All Riders &gt; {isEditMode ? "Edit Rider" : "Add New Rider"}
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            className="bg-white dark:bg-black rounded-xl shadow-sm p-6 space-y-6"
          >
            {!isEditMode && (
              <div className="rounded-lg bg-orange-50 border border-orange-200 px-4 py-3">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Required:</span> Name, Phone,
                  Email, NIN, Sticker Number, Sticker Colour and a Passport
                  Photograph. Everything else is optional.
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  This matches what the rider app asks for at signup — fields
                  marked <span className="font-semibold">*</span> are required.
                </p>
              </div>
            )}

            {/* Section: Personal Info */}
            <h2 className="text-lg font-semibold text-orange-500 mb-2">
              Personal Information
            </h2>

            {/* Profile Image Upload */}
            <div className="flex items-center gap-6 mb-6">
              <div className="relative w-24 h-24">
                <label className="w-full h-full flex items-center justify-center border-2 border-dashed border-gray-300 rounded-full cursor-pointer hover:border-orange-400">
                  {formData.profileImage ? (
                    <img
                      src={URL.createObjectURL(formData.profileImage)}
                      alt="Profile"
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <Camera className="w-8 h-8 text-gray-400" />
                  )}
                  <input
                    type="file"
                    name="profileImage"
                    className="hidden"
                    onChange={handleChange}
                    accept=".jpg,.jpeg,.png"
                  />
                </label>
              </div>
            </div>

            {/* Grid Inputs */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  name="name"
                  placeholder="Full name"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-400"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                <input
                  type="text"
                  name="phone"
                  placeholder="Phone number"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-400"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  name="email"
                  placeholder="Email address"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-400"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth <span className="text-gray-400 font-normal">(optional)</span></label>
                <input
                  type="date"
                  name="dob"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-400"
                  value={formData.dob}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Next of Kin Name <span className="text-gray-400 font-normal">(optional)</span></label>
                <input
                  type="text"
                  name="nextOfKinName"
                  placeholder="Next of kin name"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2"
                  value={formData.nextOfKinName}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Next of Kin Phone <span className="text-gray-400 font-normal">(optional)</span></label>
                <input
                  type="text"
                  name="nextOfKinPhone"
                  placeholder="Next of kin phone"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2"
                  value={formData.nextOfKinPhone}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">NIN *</label>
                <input
                  type="text"
                  name="nin"
                  placeholder="National ID number"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2"
                  value={formData.nin}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sticker Number *</label>
                <input
                  type="text"
                  name="stickerNumber"
                  placeholder="Sticker number"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2"
                  value={formData.stickerNumber}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sticker Colour *</label>
                <select
                  name="stickerColour"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 bg-white"
                  value={formData.stickerColour}
                  onChange={handleChange}
                >
                  <option value="green_white">Green & White</option>
                  <option value="blue_white">Blue & White</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Guarantor 1 Name <span className="text-gray-400 font-normal">(optional)</span></label>
                <input
                  type="text"
                  name="guarantor1Name"
                  placeholder="Guarantor 1 name"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2"
                  value={formData.guarantor1Name}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Guarantor 1 Phone <span className="text-gray-400 font-normal">(optional)</span></label>
                <input
                  type="text"
                  name="guarantor1Phone"
                  placeholder="Guarantor 1 phone"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2"
                  value={formData.guarantor1Phone}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Guarantor 2 Name <span className="text-gray-400 font-normal">(optional)</span></label>
                <input
                  type="text"
                  name="guarantor2Name"
                  placeholder="Guarantor 2 name"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2"
                  value={formData.guarantor2Name}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Guarantor 2 Phone <span className="text-gray-400 font-normal">(optional)</span></label>
                <input
                  type="text"
                  name="guarantor2Phone"
                  placeholder="Guarantor 2 phone"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2"
                  value={formData.guarantor2Phone}
                  onChange={handleChange}
                />
              </div>
              {!isEditMode && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Initial Password <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    name="password"
                    placeholder="Leave blank and the rider sets one via password reset"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2"
                    value={formData.password || ''}
                    onChange={handleChange}
                    minLength={8}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Set this to hand the rider working credentials straight away.
                    Left blank, a random password is generated and discarded, so
                    they can only sign in after completing a password reset.
                  </p>
                </div>
              )}

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Referral Code <span className="text-gray-400 font-normal">(optional)</span></label>
                <input
                  type="text"
                  name="referralCode"
                  placeholder="Referral code (optional)"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2"
                  value={formData.referralCode}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* File Uploads */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-6 cursor-pointer hover:border-orange-400">
                <Upload className="w-6 h-6 text-orange-500 mb-2" />
                <span className="text-sm font-semibold text-orange-500">
                  {isEditMode ? "Replace Driver's License" : "Upload Driver's License (optional)"}
                </span>
                <span className="text-xs text-gray-500">JPG, PNG or PDF</span>
                <input
                  type="file"
                  name="licenseFile"
                  className="hidden"
                  onChange={handleChange}
                  accept=".jpg,.jpeg,.png,.pdf"
                />
                {formData.licenseFile ? (
                  <span className="mt-2 text-xs text-gray-600">
                    {formData.licenseFile.name}
                  </span>
                ) : (
                  isEditMode && existingDocs.driverLicenseUrl && (
                    <a
                      href={existingDocs.driverLicenseUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="mt-2 text-xs text-blue-600 hover:underline"
                    >
                      View current license
                    </a>
                  )
                )}
              </label>

              <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-6 cursor-pointer hover:border-orange-400">
                <Upload className="w-6 h-6 text-orange-500 mb-2" />
                <span className="text-sm font-semibold text-orange-500">
                  {isEditMode ? "Replace Passport Picture" : "Passport Picture *"}
                </span>
                <span className="text-xs text-gray-500">JPG or PNG only</span>
                <input
                  type="file"
                  name="passportFile"
                  className="hidden"
                  onChange={handleChange}
                  accept=".jpg,.jpeg,.png"
                />
                {formData.passportFile ? (
                  <span className="mt-2 text-xs text-gray-600">
                    {formData.passportFile.name}
                  </span>
                ) : (
                  isEditMode && existingDocs.passportPhotoUrl && (
                    <a
                      href={existingDocs.passportPhotoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="mt-2 text-xs text-blue-600 hover:underline"
                    >
                      View current passport
                    </a>
                  )
                )}
              </label>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end pt-4 border-t">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg text-black font-medium ${isSubmitting
                  ? "bg-orange-400 cursor-not-allowed"
                  : "bg-orange-500 hover:bg-orange-600"
                  } transition-colors`}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="animate-spin h-4 w-4" />
                    {isEditMode ? 'Saving...' : 'Adding...'}
                  </>
                ) : (
                  isEditMode ? 'Save Changes' : 'Add Driver'
                )}
              </button>
            </div>
          </form>
        </main>
      </div>
    </div>
  );
};

export default AddNewDriver;